import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { IMeetingsService } from "services/MeetingsService";
import { MeetingModel, NewMeetingModel } from "services/api/cplapi/CPLAPI";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";

type MeetingState = {
  Meeting: MeetingModel | null;
  EditingMeeting: boolean;
  DeletingMeeting: boolean;
  LoadingMeeting: boolean;
  CreatedMeetingID: number;
};

const initialState: MeetingState = {
  Meeting: null,
  EditingMeeting: false,
  DeletingMeeting: false,
  LoadingMeeting: false,
  CreatedMeetingID: 0,
};

export const meetingsSlice = createSlice({
  name: "meetings",
  initialState,
  reducers: {
    resetData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMeeting.pending, (state) => {
        state.LoadingMeeting = true;
      })
      .addCase(createMeeting.pending, (state) => {
        state.EditingMeeting = true;
      })
      .addCase(updateMeeting.pending, (state) => {
        state.EditingMeeting = true;
      })
      .addCase(deleteMeeting.pending, (state) => {
        state.DeletingMeeting = true;
      })
      .addCase(getMeeting.rejected, (state) => {
        state.LoadingMeeting = false;
      })
      .addCase(createMeeting.rejected, (state) => {
        state.EditingMeeting = false;
      })
      .addCase(updateMeeting.rejected, (state) => {
        state.EditingMeeting = false;
      })
      .addCase(deleteMeeting.rejected, (state) => {
        state.DeletingMeeting = false;
      })
      .addCase(getMeeting.fulfilled, (state, action) => {
        state.LoadingMeeting = false;
        state.Meeting = action.payload;
      })
      .addCase(createMeeting.fulfilled, (state, action) => {
        state.EditingMeeting = false;
        state.CreatedMeetingID = action.payload;
        toast.success("Meeting created successfully");
      })
      .addCase(updateMeeting.fulfilled, (state) => {
        state.EditingMeeting = false;
        toast.success("Meeting updated successfully");
      })
      .addCase(deleteMeeting.fulfilled, (state) => {
        state.DeletingMeeting = false;
        state.EditingMeeting = false;
        toast.success("Meeting deleted successfully");
      });
  },
});

export const getMeeting = createAsyncThunk("Meetings/GetMeeting", async (meetingID: number) => {
  const meetingsService = container.get<IMeetingsService>(SERVICE_TYPES.MeetingsService);
  return await meetingsService.getMeeting(meetingID);
});
export const createMeeting = createAsyncThunk(
  "Meetings/CreateMeeting",
  async (newMeetingModel: NewMeetingModel, thunkAPI) => {
    const meetingsService = container.get<IMeetingsService>(SERVICE_TYPES.MeetingsService);
    const createdMeetingID = await meetingsService.createMeeting(newMeetingModel);
    thunkAPI.dispatch(getMeeting(createdMeetingID));
    return createdMeetingID;
  }
);

export const updateMeeting = createAsyncThunk(
  "Meetings/UpdateMeeting",
  async (meetingModel: MeetingModel, thunkAPI) => {
    const meetingsService = container.get<IMeetingsService>(SERVICE_TYPES.MeetingsService);
    await meetingsService.updateMeeting(meetingModel);
    thunkAPI.dispatch(getMeeting(meetingModel.meetingID!));
  }
);

export const deleteMeeting = createAsyncThunk("Meetings/DeleteMeeting", async (meetingID: number) => {
  const meetingsService = container.get<IMeetingsService>(SERVICE_TYPES.MeetingsService);
  await meetingsService.deleteMeeting(meetingID);
});

export const { resetData } = meetingsSlice.actions;

export const selectLoadingMeeting = (state: RootState) => {
  return state.meeting.LoadingMeeting;
};
export const selectEditingMeeting = (state: RootState) => {
  return state.meeting.EditingMeeting;
};
export const selectDeletingMeeting = (state: RootState) => {
  return state.meeting.DeletingMeeting;
};
export const selectCreatedMeetingID = (state: RootState) => {
  return state.meeting.CreatedMeetingID;
};

export const selectMeeting = (state: RootState) => {
  return state.meeting.Meeting;
};

export default meetingsSlice.reducer;
